import { useEffect, useState } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { PerformancePeriod } from "../../../../../../../../../features/optifundDashboardSlice";
import { formatCurrency, formatInteger, formatPercent } from "../../../../../formatters";
import { PerformanceRow } from "../../../../../Performance/performanceApiMapping";
import { PERIOD_LABELS } from "../../../../../Performance/PerformanceTable";
import { formatShortDisplayDate } from "../../../../mobileFormatters";
import MobileTablePagination from "../../../../MobileTablePagination";

const COLUMNS: Array<{ key: keyof PerformanceRow; label: string; format: (row: PerformanceRow) => string }> = [
  { key: "impressions", label: "Impressions", format: (r) => formatInteger(r.impressions) },
  { key: "clicks", label: "Click in", format: (r) => formatInteger(r.clicks) },
  { key: "spend", label: "Spend", format: (r) => formatCurrency(r.spend) },
  { key: "cpc", label: "CPC", format: (r) => formatCurrency(r.cpc) },
  { key: "leads", label: "Leads", format: (r) => formatInteger(r.leads) },
  { key: "qualified", label: "Qualified", format: (r) => formatInteger(r.qualified) },
  { key: "paid", label: "Paid", format: (r) => formatInteger(r.paid) },
  { key: "income", label: "Total income", format: (r) => formatCurrency(r.income) },
  { key: "profit", label: "Profit", format: (r) => formatCurrency(r.profit) },
  { key: "cpq", label: "Cost / qual", format: (r) => formatCurrency(r.cpq) },
  { key: "convRate", label: "Conv rate", format: (r) => formatPercent(r.convRate) },
];

const DATE_COL_WIDTH = 96;
const DATA_COL_WIDTH = 92;
const GRID_TEMPLATE = `${DATE_COL_WIDTH}px repeat(${COLUMNS.length}, ${DATA_COL_WIDTH}px)`;
const DEFAULT_PAGE_SIZE = 10;

interface PerformanceAccountSummaryTableProps {
  rows: PerformanceRow[];
  period: PerformancePeriod;
  isLoading: boolean;
}

// Mobile's replacement for the desktop PerformanceTable.tsx: same columns,
// same formatting/business logic, same default (chronological, as returned)
// row order - but a plain sticky-first-column horizontally-scrollable list
// instead of a sortable MUI Table, matching the mockup and Story 4 ("keep
// the first column fixed during horizontal scrolling") - no sort controls,
// the mockup doesn't show any, but paginated (rows-per-page picker + prev/
// next, defaulting to 10/page like desktop) instead of rendering every row
// at once.
export default function PerformanceAccountSummaryTable({
  rows,
  period,
  isLoading,
}: PerformanceAccountSummaryTableProps) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <Paper variant="outlined" sx={{ p: "16px 14px", borderColor: "#ececec" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#1a1a1a" }}>Account Summary</Typography>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mt: 0.25, mb: 1.5 }}>{PERIOD_LABELS[period]}</Typography>

      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", border: "1px solid #ececec", borderRadius: "10px" }}>
        <Box sx={{ minWidth: DATE_COL_WIDTH + COLUMNS.length * DATA_COL_WIDTH }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #ececec" }}>
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                p: "11px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "#8E24AA",
                backgroundColor: "#faf8fb",
                borderRight: "1px solid #f0e8f3",
              }}
            >
              Date
            </Box>
            {COLUMNS.map((column) => (
              <Box
                key={column.key}
                sx={{ p: "11px 8px", fontSize: 11, fontWeight: 700, color: "#8E24AA", textAlign: "right", backgroundColor: "#faf8fb" }}
              >
                {column.label}
              </Box>
            ))}
          </Box>

          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box sx={{ position: "sticky", left: 0, backgroundColor: "#fff", p: "11px 12px" }}>
                    <Skeleton variant="text" width="70%" />
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box key={column.key} sx={{ p: "11px 8px" }}>
                      <Skeleton variant="text" />
                    </Box>
                  ))}
                </Box>
              ))
            : pagedRows.map((row) => (
                <Box key={row.date} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "#fff",
                      borderRight: "1px solid #f4f4f4",
                      p: "11px 12px",
                      fontSize: 12.5,
                      color: "#424242",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatShortDisplayDate(row.date)}
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box
                      key={column.key}
                      sx={{
                        p: "11px 8px",
                        fontSize: 12.5,
                        textAlign: "right",
                        color: column.key === "profit" ? (row.profit > 0 ? "#2E7D32" : row.profit < 0 ? "#C62828" : "#616161") : "#616161",
                      }}
                    >
                      {column.format(row)}
                    </Box>
                  ))}
                </Box>
              ))}
        </Box>
      </Box>

      {!isLoading && (
        <MobileTablePagination
          total={rows.length}
          pageSize={pageSize}
          page={safePage}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </Paper>
  );
}
